export type TSelectOption = { value: string; label: string };

// Canonical list + the record's current (legacy free-text) value when it's not
// canonical, so opening an edit form never silently clears existing data.
// Shared by the exclusive-deals product modal (UOM/origin/state) and the
// master-product form (origin).
export const buildCanonicalOptions = (
  canon: readonly string[],
  current: string,
  labelFor: (value: string) => string = (value) => value
): TSelectOption[] => {
  const options: TSelectOption[] = canon.map((value) => ({ value, label: labelFor(value) }));
  if (current && !canon.some((value) => value.toLowerCase() === current.toLowerCase())) {
    options.unshift({ value: current, label: `${current} (legacy)` });
  }
  return options;
};

// Multi-value counterpart of buildCanonicalOptions, for `isMulti` selects
// (attributes can hold more than one value per type). Canon options come
// first; then one "(legacy)" option per stored value that isn't in canon —
// order-preserved, deduped — so every currently-selected value always has a
// matching option (multi-selects need each selected value's option present,
// unlike a single-select where the caller resolves one `current` value).
export const buildMultiCanonicalOptions = (
  canon: readonly string[],
  currentValues: readonly string[],
  labelFor: (value: string) => string = (value) => value
): TSelectOption[] => {
  const options: TSelectOption[] = canon.map((value) => ({ value, label: labelFor(value) }));
  const isCanon = (value: string) => canon.some((v) => v.toLowerCase() === value.toLowerCase());
  const seenLegacy = new Set<string>();
  currentValues.forEach((value) => {
    if (value && !isCanon(value) && !seenLegacy.has(value)) {
      seenLegacy.add(value);
      options.push({ value, label: `${value} (legacy)` });
    }
  });
  return options;
};

// Normalize a stored value to its canonical option value (e.g. 'KG' -> 'kg', 'italy' -> 'Italy').
export const toCanonicalValue = (canon: readonly string[], value: string): string => {
  if (!value) return '';
  return canon.find((v) => v.toLowerCase() === value.toLowerCase()) ?? value;
};
